/**
 * Read/edit the structured profile — `GET`/`PUT /api/profile` — the read-only
 * scoring brief that actually ranks jobs, the Refine conversation that changes
 * it, the right of access (`DownloadData`, `GET /api/account/export`), and the
 * one place the account can be erased (`DeleteAccount`, `DELETE /api/account`).
 *
 * **The two halves of this page are not the same object.** The form edits
 * `row.profile` (`UserProfile`), which is what the candidate recognises as
 * themselves. `RubricView` and `RefineChat` are about `row.rubric`, which is
 * the only thing `GET /matches`' judge ever reads. Saving the form leaves the
 * rubric alone — `OnboardingService.apply_edits` deliberately keeps it — with
 * one exception: the commute anchor on the hard-constraints card (PIVOT-232)
 * is stored on the rubric, so that one save carries both halves. This page
 * holds the whole row and updates whichever half moved.
 *
 * **This is where the hard constraints are corrected.** `ConstraintsStep` asks
 * them once during onboarding; `ProfileReview`'s `constraints` card is the only
 * later path to them, and the only path at all for a profile written before
 * that step existed (re-interviewing those candidates is out of scope).
 *
 * The layout stacks the two objects instead of columning them. PIVOT-125 put
 * the whole rubric in a ~320px `.split-rail` beside the form, which forced
 * the page's largest content into a viewport-capped scrollbox — a scrollbar
 * inside the page, sentences cut at the box fold. Now the short form leads at
 * typing width, and the brief gets the full content column below it, where
 * its two-up gates and three-axis rail have room. The `.split` pairs the
 * brief with `RefineChat` in the rail instead: the chat argues with the
 * rubric, so that is the adjacency worth a column — and the chat is short
 * enough to actually fit the sticky rail.
 *
 * **PIVOT-109's undo is a save on this page, not a chat trick.** `RefineChat`
 * threads `onUndoField` up here; `handleUndoField` merges the one reverted
 * field into the current `row.rubric` and calls the same `updateProfile` the
 * form above uses. It shares that form's error copy (`profile.saveFailed`)
 * rather than inventing a second failure message for what is, from this
 * page's point of view, the same kind of save.
 *
 * Also the one place the account's sessions can be revoked in bulk
 * (`SignOutEverywhere`, `POST /api/auth/logout-all`, PIVOT-113).
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { ChangePassword } from '../components/ChangePassword'
import { DeleteAccount } from '../components/DeleteAccount'
import { DownloadData } from '../components/DownloadData'
import { ProfileReview } from '../components/ProfileReview'
import { RefineChat } from '../components/RefineChat'
import { RubricView } from '../components/RubricView'
import { SignOutEverywhere } from '../components/SignOutEverywhere'
import { getProfile, restartOnboarding, updateProfile } from '../api/onboarding'
import { isAbortError, withMutationTimeout } from '../api/client'
import { useApiResource } from '../hooks/useApiResource'
import { useI18n } from '../i18n'
import type { CareerBaselineCorrection, Rubric, RubricField, UserProfile } from '../types/domain'
import './Profile.css'

/** PIVOT-136: opts this read into `useApiResource`'s stale-while-revalidate
 * cache, same as `Dashboard` and `SavedSeen`. */
const PROFILE_CACHE_KEY = 'profile'

export default function Profile() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const {
    data: row,
    loading,
    failed,
    reload,
    setData: setRow,
  } = useApiResource(getProfile, { cacheKey: PROFILE_CACHE_KEY })
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState(false)
  // Redo-the-interview used to navigate on the first click, with no way to
  // back out and no way back into `/profile` once inside onboarding
  // (PIVOT-145). This is the confirm step; `OnboardingSteps`' own
  // `backToProfile` link is the escape once the candidate is actually there.
  const [redoConfirmOpen, setRedoConfirmOpen] = useState(false)
  const [restarting, setRestarting] = useState(false)
  const [restartError, setRestartError] = useState(false)
  const restartRequest = useRef<AbortController | null>(null)
  // Separate from `saveError`: an undo failing while a stale success/failure
  // from the edit form is still on screen must not clear or be clobbered by
  // it, since they are two independent actions a candidate can take on this
  // page in either order.
  const [undoError, setUndoError] = useState(false)

  useEffect(
    () => () => {
      const request = restartRequest.current
      restartRequest.current = null
      request?.abort()
    },
    [],
  )

  // `editedRubric` is present only when the form edited one, which today means
  // the commute anchor on the hard-constraints card (PIVOT-232) — the region
  // gate reads that anchor beside `profile.locations`, so both halves have to
  // travel in one save or the gate stays half-blind.
  const handleSave = async (
    edited: UserProfile,
    editedRubric?: Rubric,
    editedBaseline?: CareerBaselineCorrection,
  ) => {
    setSaved(false)
    setSaveError(false)
    try {
      await updateProfile(edited, editedRubric, editedBaseline)
    } catch {
      // Say so, and leave the edits in the form: silently swallowing this is
      // how the save looked like a dead button in the first place.
      setSaveError(true)
      return
    }
    setRow((prev) =>
      prev ? { ...prev, profile: edited, rubric: editedRubric ?? prev.rubric } : prev,
    )
    setSaved(true)
    // `apply_edits` bumps `rubric.version` when the rubric actually moved, and
    // that number is on screen in `RubricView`. Re-read rather than paint our
    // own copy one version behind. An unchanged rubric is never bumped, so
    // this is the anchor-moved save and nothing else.
    // Same reason for the baseline (PIVOT-367): the server owns its version
    // and hash, and a correction moves both. Re-read rather than paint a copy
    // that still shows the identity a stored comparison was keyed on.
    if (
      editedBaseline ||
      (editedRubric && editedRubric.commute.anchor !== row?.rubric?.commute.anchor)
    ) {
      void reload()
    }
  }

  // The refine turn already persisted this server-side; patching it in is what
  // makes the rubric above the chat show the change the chat just announced.
  const handleRubricChanged = (rubric: Rubric) => {
    setRow((prev) => (prev ? { ...prev, rubric } : prev))
  }

  const handleRestart = async () => {
    if (restarting) return
    restartRequest.current?.abort()
    const controller = new AbortController()
    restartRequest.current = controller
    setRestarting(true)
    setRestartError(false)
    try {
      await restartOnboarding(withMutationTimeout(controller.signal))
    } catch (reason) {
      if (!isAbortError(reason)) setRestartError(true)
      return
    } finally {
      if (restartRequest.current === controller) {
        restartRequest.current = null
        setRestarting(false)
      }
    }
    void navigate('/onboarding')
  }

  // PIVOT-109: one field of the rubric, restored via a real `PUT /api/profile`
  // — never client-only state. Merges `revertTo` into the *current* rubric
  // (not the turn's post-turn snapshot), so undoing an earlier turn's field
  // after a later turn changed something else keeps the later change intact.
  const handleUndoField = async (
    field: RubricField,
    revertTo: Rubric[RubricField],
  ): Promise<Rubric> => {
    if (!row?.rubric) throw new Error('no rubric to undo')
    setUndoError(false)
    // The pairing of `field` and `revertTo` is the caller's invariant (it
    // always reads `revertTo` from the same field's pre-turn value) — TS
    // cannot verify that a computed key matches a value drawn from a generic
    // union, hence the assertion.
    const reverted = { ...row.rubric, [field]: revertTo } as Rubric
    try {
      const result = await updateProfile(row.profile, reverted)
      const rubric = result.rubric ?? reverted
      setRow((prev) => (prev ? { ...prev, rubric } : prev))
      return rubric
    } catch (reason) {
      setUndoError(true)
      throw reason
    }
  }

  return (
    <div>
      <title>{`${t('profile.title')} — ${t('common.appName')}`}</title>
      {/* Redo-interview is a page-level action, not a mid-scroll button: it used
          to sit between the form and the rubric, which read as buried
          (2026-08-07-plan-frontend-redesign.md §3a). It renders unconditionally,
          same as before — a transient 502 below must still leave a way back
          into onboarding (frontend/CLAUDE.md §7).

          It used to navigate on the first click — a one-click trapdoor, since
          `/onboarding` renders with no nav and restarts at step 01 (PIVOT-145).
          Now the click only opens this confirm, in the same two-step shape
          `DeleteAccount` already owns: state the restart plainly, then make the
          candidate say so a second time. */}
      <div className="page-header">
        <h1>{t('profile.title')}</h1>
        <div className="page-header-actions">
          {redoConfirmOpen ? (
            <div className="profile-redo-confirm">
              <p>{t('profile.redoConfirmBody')}</p>
              {restartError && (
                <p className="form-error" role="alert">
                  {t('profile.redoFailed')}
                </p>
              )}
              <div className="profile-redo-confirm-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={restarting}
                  aria-busy={restarting}
                  onClick={() => void handleRestart()}
                >
                  {t('profile.redoConfirmGo')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={restarting}
                  onClick={() => {
                    setRedoConfirmOpen(false)
                    setRestartError(false)
                  }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void navigate('/onboarding/matcher-v2')}
              >
                {t('profile.reviewMatcherV2')}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setRedoConfirmOpen(true)}
              >
                {t('profile.redoInterview')}
              </button>
            </>
          )}
        </div>
      </div>

      {loading && <p role="status">{t('common.loading')}</p>}
      {/* Without the retry, a transient 502 leaves this page as a header and an
          error line — redo-the-interview above and delete-my-account below both
          still render unconditionally (frontend/CLAUDE.md §7). */}
      {failed && (
        <p className="form-error" role="alert">
          {t('common.error')}{' '}
          <button type="button" className="btn btn-ghost" onClick={() => void reload()}>
            {t('common.retry')}
          </button>
        </p>
      )}

      {row && (
        <>
          {/* Typing width, not page width — a résumé textarea stretched across
              the whole content column reads like a banner, not a form. */}
          <div className="profile-form">
            <ProfileReview
              profile={row.profile}
              // PIVOT-232: the only place the hard constraints can be corrected
              // after onboarding — and the only path at all for a profile
              // written before that step existed, since re-interviewing those
              // candidates is out of scope. The rubric rides along because the
              // commute anchor is stored on it.
              constraints
              rubric={row.rubric}
              // PIVOT-367: the only place after onboarding where the job the
              // person actually does can be confirmed or corrected. The
              // evidence rides along because a corrected job has to point at
              // a source this account already holds.
              careerBaseline={row.career_baseline}
              candidateEvidence={row.candidate_evidence}
              onSave={handleSave}
              // This page saves edits. It does not fetch matches — the dashboard
              // does — so it must not borrow onboarding's "See my first matches".
              submitLabel="profile.saveChanges"
              // Nor onboarding's "Your profile" / "…before continuing" — this
              // page already has its own `<h1>` and nothing to continue to
              // (PIVOT-145).
              heading="profile.formTitle"
              body="profile.formBody"
              // Rendered inside the form, under the button: the outcome belongs
              // where the click was, not above a form long enough to scroll past.
              feedback={
                <>
                  {saved && <p className="form-success">{t('profile.saved')}</p>}
                  {saveError && <p className="form-error">{t('profile.saveFailed')}</p>}
                </>
              }
            />
          </div>

          {/* `.split` (layout.css, PIVOT-124): the scoring brief is the primary
              column, the chat that edits it sits in the sticky rail beside it —
              see the module docstring for why the brief is NOT the rail. Below
              640px this stacks in DOM order: brief, then chat. No rubric yet →
              no columns: `RubricView` renders its own empty-state copy at full
              width, and reserving a rail for a chat that cannot exist (the
              endpoint 400s without a rubric) would just indent that copy. */}
          {row.rubric ? (
            <div className="split profile-scoring">
              <div className="split-main">
                <RubricView rubric={row.rubric} />
              </div>
              <div className="split-rail">
                <RefineChat
                  rubric={row.rubric}
                  onRubricChanged={handleRubricChanged}
                  onUndoField={handleUndoField}
                />
                {/* Belongs to the chat above it, not the edit form — undo is a
                    save an undo button on this screen triggered, and the error
                    should sit where that action was. */}
                {undoError && <p className="form-error">{t('profile.saveFailed')}</p>}
              </div>
            </div>
          ) : (
            <div className="profile-scoring">
              <RubricView rubric={null} />
            </div>
          )}
        </>
      )}

      {/* Quiet strip, separated from the primary edit flow above by a rule —
          erasure is rare and destructive and should not read as just the next
          section of the form (plan §3a, ticket PIVOT-125). Named by an
          eyebrow rather than three floating h3s with nothing above them
          (PIVOT-145) — that read as a generic template footer. The page owns
          the spacing between the three components now; none of them sets its
          own margin. */}
      <div className="profile-account">
        <p className="eyebrow">{t('profile.accountSectionLabel')}</p>
        <DownloadData />
        <ChangePassword />
        <SignOutEverywhere />
        <DeleteAccount />
      </div>
    </div>
  )
}
